// Простий i18n з 4 мовами: es (default), uk, ca, en.
// Зберігає вибір мови у файловій БД (app_prefs.json) через storageService
// та дублює у localStorage як fallback для веб/розробки.

// Імпортуємо всі мови ПЕРШИМИ
import es from './es.json';
import uk from './uk.json';
import ca from './ca.json';
import en from './en.json';

import { useEffect, useState } from 'react';
import { getData, saveData } from '../services/storageService';

// ---------- ВНУТРІШНІ ДОПОМОЖНІ ---------------------------------------------
const FALLBACK_LANG = 'es';
const STORAGE_KEY = 'lang';

function softGet(obj, key) { return obj && Object.prototype.hasOwnProperty.call(obj, key) ? obj[key] : undefined; }

const STR = { es, uk, ca, en };

// ---- Стан мови + слухачі ---------------------------------------------------
let currentLang = FALLBACK_LANG;
try {
  const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
  if (raw && STR[raw]) currentLang = raw;
} catch { /* ignore */ }

const listeners = new Set();

// ---- Допоміжні функції -----------------------------------------------------
function getDeep(obj, path) {
  if (!obj || !path) return undefined;
  const parts = String(path).split('.');
  let cur = obj;
  for (const p of parts) {
    if (cur && Object.prototype.hasOwnProperty.call(cur, p)) cur = cur[p];
    else return undefined;
  }
  return cur;
}

// ---- Публічні API ----------------------------------------------------------
export function t(key, vars) {
  const table = STR[currentLang] || STR[FALLBACK_LANG];

  // 1) deep path a.b.c
  let text = getDeep(table, key);
  if (text == null) text = getDeep(STR[FALLBACK_LANG], key);

  // 2) legacy flat key
  if (text == null) text = softGet(table, key);
  if (text == null) text = softGet(STR[FALLBACK_LANG], key);

  // 3) fallback to key
  if (text == null) text = key;

  if (vars && typeof vars === 'object') {
    for (const [k, v] of Object.entries(vars)) {
      text = String(text).replaceAll(`{${k}}`, String(v));
    }
  }
  return text;
}

export function getLang() {
  return currentLang;
}

export function setLang(lang) {
  const next = STR[lang] ? lang : FALLBACK_LANG;
  
  if (currentLang !== next) {
    currentLang = next;

    // Оповістити всіх підписників про зміну мови
    listeners.forEach(fn => { 
      try { 
        fn(currentLang); 
      } catch {} 
    });

    // Зберегти у файловій БД + дублювати у localStorage
    (async () => {
      try {
        const prefs = await getData('app_prefs');
        const updated = { ...(prefs && typeof prefs === 'object' ? prefs : {}), lang: currentLang };
        await saveData('app_prefs', updated);
      } catch { /* ignore */ }

      try {
        if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, currentLang);
      } catch { /* ignore */ }
    })();
  }
}

// Примітивний хук для реактивності мови в компонентах
export function useI18n() {
  const [lang, set] = useState(currentLang);

  useEffect(() => {
    const fn = (l) => set(l);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);

  useEffect(() => {
    // При першому монтуванні: підтягнемо мову з файлової БД (якщo є)
    let alive = true;
    (async () => {
      try {
        const prefs = await getData('app_prefs');
        const stored = prefs?.lang;
        if (alive && stored && STR[stored] && stored !== currentLang) {
          currentLang = stored;
          set(stored);
          // Оповістити всіх підписників
          listeners.forEach(fn => { 
            try { 
              fn(currentLang); 
            } catch {} 
          });
        }
      } catch { /* no prefs is ok */ }
    })();
    return () => { alive = false; };
  }, []);

  return { t, lang, setLang };
}

// ---------- Додаткові утиліти для About: версія та ліцензії -----------------

// універсально дістаємо package.json (Electron preload або public/)
async function readPackageJson() {
  // 1) Electron preload (типові назви мостів)
  try { if (window.api?.app?.getPackage) return await window.api.app.getPackage(); } catch {}
  try { if (window.electronAPI?.getPackageJson) return await window.electronAPI.getPackageJson(); } catch {}

  // 2) DEV / Web — public/package.json
  try {
    const res = await fetch('/package.json', { cache: 'no-store' });
    if (res.ok) return await res.json();
  } catch {}

  // 3) Нічого не вийшло
  return null;
}

// Повертає { name, version, license } застосунку
export async function getAppMeta() {
  const pkg = await readPackageJson();
  return {
    name: pkg?.name || 'app',
    version: pkg?.version || '0.0.0',
    license: pkg?.license || 'UNLICENSED',
  };
}

// Повертає список залежностей з package.json: { deps:[], devDeps:[] }
// Кожен елемент: { name, version }  (ліцензії саме пакетів не витягуємо без файлового доступу node_modules)
export async function getLibLicenses() {
  const pkg = await readPackageJson();
  const deps = [];
  const devDeps = [];

  if (pkg?.dependencies && typeof pkg.dependencies === 'object') {
    for (const [name, version] of Object.entries(pkg.dependencies)) deps.push({ name, version });
  }
  if (pkg?.devDependencies && typeof pkg.devDependencies === 'object') {
    for (const [name, version] of Object.entries(pkg.devDependencies)) devDeps.push({ name, version });
  }

  // відсортуємо алфавітно для охайності
  deps.sort((a, b) => a.name.localeCompare(b.name));
  devDeps.sort((a, b) => a.name.localeCompare(b.name));

  return { deps, devDeps };
}

export { STR as translations };


